import type * as React from "react";
import type { Callback } from "shelving";
import { Component } from "react";
import { getOptionalErrorCode } from "shelving";
import { Aside } from "./Aside.js";
import { Retrier, RetryButton } from "./Retrier.js";

export const ERROR_ASIDE = (
	<Aside>
		<p>Unknown error</p>
		<RetryButton mods="button-quiet" />
	</Aside>
);

export const MISSING_ASIDE = (
	<Aside>
		<p>Deleted content</p>
		<RetryButton mods="button-quiet" />
	</Aside>
);

export const PRIVATE_ASIDE = (
	<Aside>
		<p>Private content</p>
		<RetryButton mods="button-quiet" />
	</Aside>
);

type CatchProps = {
	/** Message shown if this `<Catch>` catches an unknown error. */
	error?: React.ReactElement;
	/** Message shown if this `<Catch>` catches a forbidden error. */
	forbidden?: React.ReactElement;
	/** Message shown if this `<Catch>` catches a required error. */
	required?: React.ReactElement;
	/** Content that's shown if there's no error. */
	children: React.ReactNode;
};

type CatchState = {
	/** The error that was caught. */
	reason: unknown;
};

/**
 * React component that provides an Error Boundary.
 * If an error occurs in any component under this, a general error will be shown to the user.
 */
export class Catcher extends Component<CatchProps, CatchState> {
	static defaultProps: CatchProps = {
		error: ERROR_ASIDE,
		forbidden: PRIVATE_ASIDE,
		required: MISSING_ASIDE,
		children: null,
	};
	override state: CatchState = {
		reason: undefined,
	};
	readonly retry: Callback = () => {
		this.setState({ reason: undefined });
	};
	static getDerivedStateFromError(reason: unknown): CatchState {
		return { reason };
	}
	override render(): React.ReactNode {
		const { retry, state, props } = this;
		const { reason } = state;
		const { error, forbidden, required, children } = props;
		if (reason === undefined) return children;
		const code = getOptionalErrorCode(reason);
		return <Retrier retry={retry}>{code === "permission-denied" ? forbidden : code === "not-found" ? required : error}</Retrier>;
	}
}
